/**
 * Grid bookcase constraints (plan §4.1): named hard errors and advisory warnings.
 * Field names: `W H D n openings opening-<i> back <partId>` for bay 1 (the first slice's),
 * `bays bayWidths bay-<b>/n bay-<b>/openings bay-<b>/opening-<i>` (b 1-based) for bays 2…N,
 * `plinth`. Warnings never share a field with an error.
 */
import type { Issue, IssueFix } from '../assembly';
import type { Design } from '../../domain/schemas';
import type { Limits } from '../../domain/profiles';
import type { ConstructionProfile } from '../../domain/profiles';
import { fmtMm, sum } from '../../domain/units';
import type { AnyStock } from '../stock';
import { bayAvailableTicks, bayCount, layoutOf, placeRemainder, setBays, setDimension, setEven, setShelfCount, setStock } from './design';
import { THIN_STOCK_ID } from '../build-common';

const WORD = { W: 'width', H: 'height', D: 'depth' } as const;

/** The patch keys a grid change touched (top-level design keys only, the way the UI applies them). */
function patchOf(before: Design, after: Design, keys: readonly string[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of keys) {
    const a = (after as Record<string, unknown>)[k];
    if (JSON.stringify(a) !== JSON.stringify((before as Record<string, unknown>)[k]) && a !== undefined) out[k] = a;
  }
  return out;
}
const LAYOUT_KEYS = ['W', 'H', 'D', 'n', 'even', 'openings', 'bays', 'bayWidths', 'bayLayouts'] as const;

/** A fix is computed from a malformed design too: never let it throw (no fix instead). */
function safe<T>(f: () => T): T | null {
  try { return f(); } catch { return null; }
}

/** Field name for a per-bay field: bay 0 keeps the first slice's names. */
export const bayField = (bay: number, base: string): string => (bay <= 0 ? base : `bay-${bay + 1}/${base}`);

export interface GridMeasures {
  t: number;
  b: number;
  plinth: number;
  bayWidths: number[];
  availables: number[];
  limits: Limits;
  construction: ConstructionProfile;
  speciesText: string;
  /** The stock the design is built from (for the fixes' refits). */
  stock?: AnyStock;
}

/** Hard errors on the parameters (before any part is laid out). */
export function parameterErrors(design: Design, m: GridMeasures): Issue[] {
  const { W, H, D } = design;
  const L = m.limits;
  const errors: Issue[] = [];
  const bays = bayCount(design);
  const bayRules = m.construction.bays;
  const maxW = bays === 1 ? L.W.max : Math.min(bayRules?.maxOverallWTicks ?? L.W.max * bays, L.W.max * bays);
  const rangeMsg = (key: 'W' | 'H' | 'D', v: number) => {
    const lim = key === 'W' ? { ...L.W, max: maxW } : L[key];
    if (!Number.isFinite(v) || !Number.isInteger(v)) {
      errors.push({ field: key, message: `${lim.label} must be a number of millimetres.` });
    } else if (v < lim.min || v > lim.max) {
      // fix: the nearest allowed size, with the openings (and bay widths) refitted as Shape does
      const c = Math.min(lim.max, Math.max(lim.min, Math.round(v / 10) * 10));
      const after = safe(() => setDimension(design, key, c, m.stock));
      const fix: IssueFix = { label: `Set ${WORD[key]} to ${fmtMm(c)} mm`, patch: after ? patchOf(design, after, LAYOUT_KEYS) : { [key]: c } };
      errors.push({
        field: key,
        message: bays === 1 || key !== 'W'
          ? `${lim.label} must be between ${fmtMm(lim.min)} and ${fmtMm(lim.max)} mm for this construction profile.`
          : `Width must be between ${fmtMm(lim.min)} and ${fmtMm(lim.max)} mm for ${bays} bays.`,
        fix,
      });
    }
  };
  rangeMsg('W', W); rangeMsg('H', H); rangeMsg('D', D);

  if (bayRules && (bays < bayRules.min || bays > bayRules.max)) errors.push({ field: 'bays', message: `Bays must be between ${bayRules.min} and ${bayRules.max}.` });
  if (bays > 1) {
    const widths = m.bayWidths;
    const total = W - (bays + 1) * m.t;
    if (!design.bayWidths || design.bayWidths.length !== bays) errors.push({ field: 'bayWidths', message: `${bays} bay widths are needed; ${design.bayWidths?.length ?? 0} are set.` });
    else if (sum(widths) !== total) {
      const r = total - sum(widths);
      errors.push({ field: 'bayWidths', message: r > 0 ? `Bay widths add up to ${fmtMm(sum(widths))} mm; ${fmtMm(r)} mm left to place.` : `Bay widths add up to ${fmtMm(sum(widths))} mm; that is ${fmtMm(-r)} mm too much (they must total ${fmtMm(total)} mm).` });
    }
    const minBay = bayRules?.minBayClearTicks ?? 2500;
    // fix (stress-v2 v007: a fitted middle bay of 158 mm left Shape with no way on): equal bays when that gives every
    // bay the minimum, else one bay fewer, equal
    const equal = (n: number) => { const base = Math.floor(total / n); return Array.from({ length: n }, (_x, i) => base + (i < total - base * n ? 1 : 0)); };
    const narrowFix = (): IssueFix | undefined => {
      if (equal(bays)[bays - 1]! >= minBay) return { label: 'Make the bays equal', patch: { bayWidths: equal(bays) } };
      if (bays <= 1) return undefined;
      const n = bays - 1;
      if (Math.floor((W - (n + 1) * m.t) / n) < minBay) return undefined;
      const after = safe(() => setBays(design, n, m.stock));
      return after ? { label: `Use ${n} ${n === 1 ? 'bay' : 'bays'}`, patch: patchOf(design, after, LAYOUT_KEYS) } : undefined;
    };
    widths.forEach((w, i) => {
      if (w >= minBay) return;
      const fix = narrowFix();
      errors.push({ field: 'bayWidths', message: `Bay ${i + 1} is ${fmtMm(w)} mm wide; the narrowest bay is ${fmtMm(minBay)} mm.`, ...(fix ? { fix } : {}) });
    });
    if ((design.bayLayouts?.length ?? 0) !== bays - 1) errors.push({ field: 'bays', message: `${bays} bays need ${bays - 1} more bay layouts; ${design.bayLayouts?.length ?? 0} are set.` });
  }
  if (m.plinth && bayRules && m.plinth > bayRules.maxPlinthTicks) errors.push({ field: 'plinth', message: `The plinth can be at most ${fmtMm(bayRules.maxPlinthTicks)} mm high.`, fix: { label: `Use a ${fmtMm(bayRules.maxPlinthTicks)} mm plinth`, patch: { plinth: bayRules.maxPlinthTicks } } });

  for (let bay = 0; bay < bays; bay++) {
    const { n, openings } = layoutOf(design, bay);
    const f = (base: string) => bayField(bay, base);
    const shelvesLabel = bays === 1 ? 'Shelves' : `Bay ${bay + 1} shelves`;
    if (!Number.isInteger(n) || n < L.n.min || n > L.n.max) errors.push({ field: f('n'), message: `${shelvesLabel} must be between ${L.n.min} and ${L.n.max}.` });
    const list: number[] = Array.isArray(openings) ? openings : [];
    const count = (Number.isInteger(n) && n >= 0 ? n : 0) + 1;
    const A = m.availables[bay]!;
    const openingsSum = sum(list);
    const remainder = A - openingsSum;
    if (list.length !== count) errors.push({ field: f('openings'), message: `${count} openings are needed for ${n} shelves; ${list.length} are set.` });
    // fixes for openings under the minimum: space them evenly when that is enough, else one shelf
    // fewer, else the thinner stock (each refitted the way Shape does it)
    const lowFix = (): IssueFix | undefined => {
      const ok = (d: Design | null) => !!d && layoutOf(d, bay).openings.every((g) => g >= L.minOpening) && sum(layoutOf(d, bay).openings) === bayAvailableTicks(d, bay, m.stock);
      const even = safe(() => setEven(design, true, m.stock, bay));
      if (A >= count * L.minOpening && ok(even)) return { label: `Space the openings evenly (${fmtMm(Math.floor(A / count / 10) * 10)} mm each)`, patch: patchOf(design, even!, LAYOUT_KEYS) };
      const fewer = Number.isInteger(n) && n > 0 ? safe(() => setShelfCount(design, n - 1, m.stock, bay)) : null;
      if (ok(fewer)) return { label: `Remove a shelf (openings become ${fmtMm(Math.floor(layoutOf(fewer!, bay).openings[0]! / 10) * 10)} mm)`, patch: patchOf(design, fewer!, LAYOUT_KEYS) };
      const thin = design.stockProfileId !== THIN_STOCK_ID ? safe(() => setStock(design, THIN_STOCK_ID)) : null;
      if (ok(thin)) return { label: 'Use thinner stock (birch plywood 18 mm)', patch: { stockProfileId: THIN_STOCK_ID, ...patchOf(design, thin!, LAYOUT_KEYS) } };
      return undefined;
    };
    if (Number.isFinite(A) && A < count * L.minOpening) {
      const fix = lowFix();
      errors.push({ field: f('n'), message: `Too many shelves for this height: ${count} openings need at least ${fmtMm(count * L.minOpening)} mm, but only ${fmtMm(Math.max(0, A))} mm is available.`, ...(fix ? { fix } : {}) });
    }
    let low: IssueFix | undefined | null = null;
    list.forEach((g, i) => {
      const label = bays === 1 ? `Opening ${i + 1}` : `Bay ${bay + 1} opening ${i + 1}`;
      if (!Number.isInteger(g)) errors.push({ field: f(`opening-${i}`), message: `${label} must be set to 0.1 mm at most.` });
      else if (g < L.minOpening) {
        if (low === null) low = lowFix();
        errors.push({ field: f(`opening-${i}`), message: `${label} is ${fmtMm(g)} mm; the minimum clear height is ${fmtMm(L.minOpening)} mm.`, ...(low ? { fix: low } : {}) });
      }
    });
    if (list.length === count && Number.isFinite(remainder) && remainder !== 0) {
      const lead = bays === 1 ? 'Openings' : `Bay ${bay + 1} openings`;
      const placed = safe(() => placeRemainder(design, m.stock, bay));
      const fixOk = placed && layoutOf(placed.design, bay).openings.every((g) => g >= L.minOpening);
      errors.push({
        ...(fixOk ? { fix: { label: placed!.label, patch: patchOf(design, placed!.design, LAYOUT_KEYS) } } : ((f) => (f ? { fix: f } : {}))(lowFix())),
        field: f('openings'),
        message: remainder > 0
          ? `${lead} add up to ${fmtMm(openingsSum)} mm; ${fmtMm(remainder)} mm left to place.`
          : `${lead} add up to ${fmtMm(openingsSum)} mm; that is ${fmtMm(-remainder)} mm too much (they must total ${fmtMm(A)} mm).`,
      });
    }
  }
  if (Number.isFinite(D) && D - m.b <= 0) errors.push({ field: 'D', message: 'Depth must be greater than the back thickness.' });
  return errors;
}

/** Advisory warnings (sag, anti-tip). */
export function advisories(design: Design, m: GridMeasures): Issue[] {
  const warnings: Issue[] = [];
  const L = m.limits;
  const bays = bayCount(design);
  for (let bay = 0; bay < bays; bay++) {
    const span = m.bayWidths[bay]!;
    if (layoutOf(design, bay).n > 0 && span > L.maxShelfSpanWarning) {
      // fix: one more bay (a divider), when every bay stays at least the narrowest allowed
      const more = bays + 1;
      const minBay = m.construction.bays?.minBayClearTicks ?? 2500;
      const maxBays = m.construction.bays?.max ?? 4;
      const each = (design.W - (more + 1) * m.t) / more;
      const after = Number.isFinite(each) && more <= maxBays && each >= minBay ? safe(() => setBays(design, more, m.stock)) : null;
      warnings.push({
        ...(after ? { fix: { label: `Add a divider (${more} bays)`, patch: patchOf(design, after, LAYOUT_KEYS) } } : {}),
        field: bays === 1 ? 'W' : 'bayWidths',
        message: bays === 1
          ? `Shelves span ${fmtMm(span)} mm. In ${fmtMm(m.t)} mm ${m.speciesText} under a full load of books they may sag over time; the maker may suggest a stiffener.`
          : `Bay ${bay + 1} shelves span ${fmtMm(span)} mm. In ${fmtMm(m.t)} mm ${m.speciesText} under a full load of books they may sag over time; narrow the bay or add a divider.`,
      });
    }
  }
  if (design.H > L.antiTipAbove) warnings.push({ field: 'H', message: `At ${fmtMm(design.H)} mm tall this must be fixed to the wall (anti-tip).` });
  return warnings;
}
